import {
  CellRule,
  CellRulePanel,
  CellRulePanelUpdates,
  CellRulePanelWithRules,
  CellRuleStudyId,
  UpdateCellRule,
} from 'interfaces/cellRule';
import { MultiplexPanelCellRuleSnapshot, MultiplexPanelCellRules } from 'interfaces/study';
import { apiRequestHandlerPromise } from 'utils/apiRequestHandler';

export const getCellPanels = async (studyId: CellRuleStudyId, signal?: AbortSignal): Promise<CellRulePanel[]> => {
  return apiRequestHandlerPromise<CellRulePanel[]>({
    url: `studies/${studyId}/cell_panels`,
    method: 'GET',
    signal,
  });
};

export const getCellRules = async (
  panelId: string,
  studyId: string,
  signal?: AbortSignal
): Promise<CellRulePanelWithRules> => {
  return apiRequestHandlerPromise<CellRulePanelWithRules>({
    url: `studies/${studyId}/cell_panels/${panelId}/rules`,
    method: 'GET',
    signal,
  });
};

export const updateCellRule = ({
  panelId,
  updateModel,
  studyId,
  signal,
}: {
  panelId: string;
  updateModel: UpdateCellRule;
  studyId: CellRuleStudyId;
  signal?: AbortSignal;
}) => {
  return apiRequestHandlerPromise<CellRule>({
    url: `studies/${studyId}/cell_panels/${panelId}/rules`,
    method: 'PUT',
    signal,
    data: JSON.stringify(updateModel),
  });
};

interface CellRulePanelRequest {
  panel: CellRulePanelUpdates;
  globalPanelFilters?: {
    cellTypeIds?: string[];
    stainTypeIds?: string[];
  };
}

export const createCellPanel = ({
  studyId,
  panel,
  signal,
}: {
  studyId: CellRuleStudyId;
  panel: CellRulePanelUpdates;
  signal?: AbortSignal;
}) => {
  const requestData: CellRulePanelRequest = {
    panel,
  };

  return apiRequestHandlerPromise<CellRulePanelWithRules>({
    url: `studies/${studyId}/cell_panels`,
    method: 'POST',
    signal,
    data: JSON.stringify(requestData),
  });
};

export const updateCellPanel = ({
  panelId,
  studyId,
  panelUpdates,
  signal,
}: {
  panelId: string;
  studyId: CellRuleStudyId;
  panelUpdates?: CellRulePanelUpdates;
  signal?: AbortSignal;
}) => {
  return apiRequestHandlerPromise<CellRulePanel>({
    url: `studies/${studyId}/cell_panels/${panelId}`,
    method: 'PATCH',
    signal,
    data: JSON.stringify({
      panelUpdates,
    }),
  });
};

export const deleteCellRulePanel = ({ studyId, panelId }: { studyId: string; panelId: string }) => {
  return apiRequestHandlerPromise({
    url: `studies/${studyId}/cell_panels/${panelId}`,
    method: 'DELETE',
  });
};
export const createCellRuleRevision = ({
  studyId,
  panelId,
  rules,
  signal,
}: {
  studyId: string;
  panelId?: string | null;
  rules: MultiplexPanelCellRuleSnapshot[];
  signal?: AbortSignal;
}) => {
  return apiRequestHandlerPromise<MultiplexPanelCellRules>({
    url: `studies/${studyId}/cell_panels/revisions`,
    method: 'POST',
    signal,
    data: JSON.stringify({
      ...(panelId ? { panelId } : {}),
      rules,
    }),
  });
};

export const approveCellRulePanel = ({
  approved,
  studyId,
  panelId,
  signal,
}: {
  approved: boolean;
  studyId: string;
  panelId: string;
  signal?: AbortSignal;
}) => {
  return apiRequestHandlerPromise<CellRulePanel>({
    url: `studies/${studyId}/cell_panels/${panelId}`,
    method: 'PATCH',
    signal,
    data: JSON.stringify({
      panelUpdates: { approved: approved },
    }),
  });
};

type Match = {
  id: string;
  displayName?: string; // Optional
  method: 'bedrock'; // Literal type
};

type Result = {
  input: string;
  type: 'stain' | 'cell'; // Literal union
  match?: Match; // Optional
  reason?: string; // Optional
};

export type MapResp = {
  results: Result[];
  modelUsed: string;
};

export const fetchBestMatchIds = ({
  studyId,
  cellIds,
  stainIds,
  signal,
}: {
  studyId: string;
  cellIds: string[];
  stainIds: string[];
  signal?: AbortSignal;
}) => {
  return apiRequestHandlerPromise<MapResp>({
    url: `studies/${studyId}/cell_panels/match-ids.get`,
    method: 'POST',
    signal,
    data: JSON.stringify({
      cellIds,
      stainIds,
    }),
  });
};
